import { IFrameActions } from "src/constants";
import { sendMessageToParentApp } from "src/utils";

/**
 * How long to wait for the host app's answer before giving up.
 *
 * The host has to complete an OIDC silent renew (a hidden iframe round trip to
 * the identity server), so this is deliberately generous. It exists mainly so a
 * host build that predates this handshake — and therefore never answers — does
 * not leave the failed request hanging forever.
 */
const REFRESH_TIMEOUT_MS = 20000;

/** In-flight round trip, shared by every request that 401s at the same time. */
let pendingRefresh = null;

let requestSeq = 0;

/**
 * Ask the host commerce app to silently renew the session and hand back the
 * refreshed user.
 *
 * Concurrent 401s share a single round trip: a page that fires several calls at
 * once would otherwise ask the host to renew several times over, and the extra
 * silent renews can invalidate each other's refresh token.
 *
 * @returns {Promise<object|null>} the refreshed ftxCoreUser, or null when the
 *   host could not renew (or did not answer).
 */
export function requestParentTokenRefresh() {
  if (pendingRefresh) return pendingRefresh;

  const requestId = `lns-token-refresh-${++requestSeq}-${Date.now()}`;

  pendingRefresh = new Promise((resolve) => {
    let settled = false;
    let timeoutId = null;

    const finish = (user) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onHostMessage);
      if (timeoutId) clearTimeout(timeoutId);
      resolve(user || null);
    };

    function onHostMessage(event) {
      const message = event?.data,
        action = message?.action,
        value = message?.value;

      if (
        action !== IFrameActions.AUTH_TOKEN_REFRESHED &&
        action !== IFrameActions.AUTH_TOKEN_REFRESH_FAILED
      ) {
        return;
      }

      // Ignore an answer to an earlier round trip that arrived late.
      if (value?.requestId && value.requestId !== requestId) return;

      finish(
        action === IFrameActions.AUTH_TOKEN_REFRESHED
          ? value?.ftxCoreUser || null
          : null
      );
    }

    window.addEventListener("message", onHostMessage);

    timeoutId = setTimeout(() => finish(null), REFRESH_TIMEOUT_MS);

    sendMessageToParentApp({
      action: IFrameActions.AUTH_TOKEN_REFRESH_REQUEST,
      value: { requestId },
    });
  }).finally(() => {
    pendingRefresh = null;
  });

  return pendingRefresh;
}
